import { useContext, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { FaEye } from "react-icons/fa";
import { Button } from "react-bootstrap";
import { request } from "../../../utils/api";
import { toast } from "react-toastify";
import { formatYYYYMMDD } from "../../../utils";
import DataTable from "react-data-table-component";
import AddLabelModal from "../modal/AddLabelModal";
import ViewUserModal from "../modal/ViewUserModal";
import Loading from "../utilComponent/Loading";
import { AbilityContext } from "../../../context/AbilityContext";

const ApproveWorkflow = () => {
  const navigate = useNavigate();
  const { date } = useParams();
  const ability = useContext(AbilityContext);

  const [searchText, setSearchText] = useState("");
  const [trackingList, setTrackingList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);

  const [showAddModal, setShowAddModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedWarehouse, setSelectedWarehouse] = useState(null);
  const [showApproveAllModal, setShowApproveAllModal] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);

  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 15,
    total: 0,
    last_page: 1,
  });

  const handleView = (row) => {
    setSelectedWarehouse(row);
    setShowViewModal(true);
  };

  const handleSearch = (e) => {
    setSearchText(e.target.value.toLowerCase());
  };

  const handlePageChange = (page) => {
    setPagination((prev) => ({ ...prev, current_page: page }));
    getTrackingList(page);
  };

  const updateLabelStatus = async (row, status) => {
    if (!status) return;

    const fd = new FormData();
    fd.append("approved", status);

    try {
      setStatusLoading(true);

      await request({
        url: `labels/${row.label_id}/update-approved`,
        method: "POST",
        data: fd,
      });

      setTrackingList((prev) =>
        prev.map((item) =>
          item.label_id === row.label_id
            ? { ...item, status }
            : item
        )
      );
    } catch (error) {
      toast.error("Something went wrong");
    } finally {
      setStatusLoading(false);
    }
  };

  const handleTrackingStatusChange = (row, status) => {
    updateLabelStatus(row, status);
  };

  const getTrackingList = async (page = 1) => {
    try {
      setLoading(true);

      const response = await request({
        url: `labels-date-wise?approved=0`,
        method: "GET",
        params: {
          page,
          per_page: pagination.per_page,
          from_date: date,
          to_date: date,
        },
      });

      if (response?.status) {
        setTrackingList(transformData(response.data.items || []));
        setPagination(response.data.pagination);
      }
    } catch (error) {
      toast.error("Failed to load tracking list");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getTrackingList(pagination.current_page);
    // eslint-disable-next-line
  }, []);

  const transformData = (data = []) =>
    data.flatMap((item) =>
      item.labels.map((label) => ({
        label_id: label.id,
        label_name: label.label_name,
        label_extract_no: label.label_extract_no,
        label_upload_date: formatYYYYMMDD(label.created_at),
        product_qty: label.product_qty,
        status: label.status,
        product_name: label.product?.name,
        warehouse_name: label.product?.warehouse?.name,
        user_name: label.user?.name,
        created_at: label.created_at,
      }))
    );

  const filteredProducts = trackingList.filter((item) =>
    Object.values(item).some((val) =>
      String(val).toLowerCase().includes(searchText)
    )
  );

  const columns = [
    { name: "Warehouse", selector: (row) => row.warehouse_name, sortable: true },
    { name: "Product", selector: (row) => row.product_name, sortable: true },
    { name: "QTY", selector: (row) => row.product_qty, sortable: true },
    { name: "User", selector: (row) => row.user_name, sortable: true },
    { name: "Label", selector: (row) => row.label_name, sortable: true },
    { name: "Extract No", selector: (row) => row.label_extract_no },
    { name: "Status", selector: (row) => row.status === 1 ? "Approved" : "Pending", sortable: true },
    { name: "Upload Date", selector: (row) => row.label_upload_date },
    {
      name: "View",
      width: "80px",
      cell: (row) => (
        <button
          className="btn btn-sm btn-outline-secondary"
          onClick={() => handleView(row)}
        >
          <FaEye />
        </button>
      ),
    },
    {
      name: "Update Status",
      width: "160px",
      cell: (row) => (
        ability.can("updateWorkflowStatus", "all") ? (
          <select
            className="form-select form-select-sm"
            disabled={statusLoading}
            value={row.status || ""}
            onChange={(e) =>
              handleTrackingStatusChange(row, e.target.value)
            }
          >
            <option value="">Select</option>
            <option value="1">Approved</option>
          </select>
        ) : (
          <span className="text-muted">No Permission</span>
        )
      ),
    }

  ]

  const handleApproveAll = async () => {
    try {
      setBulkLoading(true);
      setStatusLoading(true);

      for (const row of trackingList) {
        if (row.status === 1) continue; // already approved

        const fd = new FormData();
        fd.append("approved", 1);

        await request({
          url: `labels/${row.label_id}/update-approved`,
          method: "POST",
          data: fd,
        });
      }

      setTrackingList((prev) =>
        prev.map((item) => ({ ...item, status: 1 }))
      );

      toast.success("All labels approved successfully");
    } catch (error) {
      toast.error("Failed to approve all labels");
    } finally {
      setBulkLoading(false);
      setStatusLoading(false);
      setShowApproveAllModal(false);
    }
  };


  return (
    <>
      {statusLoading && (
        <div className="fullscreen-loader">
          <Loading />
        </div>
      )}
      <style>
        {`
          .fullscreen-loader {
            position: fixed;
            inset: 0;
            background: rgba(255, 255, 255, 0.7);
            z-index: 9999;
            display: flex;
            align-items: center;
            justify-content: center;
          }
        `}
      </style>

      {showAddModal && (
        <AddLabelModal
          show={showAddModal}
          setShow={setShowAddModal}
          trackingList={trackingList}
          setTrackingList={setTrackingList}
        />
      )}

      {showApproveAllModal && (
        <div className="modal fade show d-block" tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirm Approval</h5>
                <button
                  className="btn-close"
                  onClick={() => setShowApproveAllModal(false)}
                />
              </div>

              <div className="modal-body">
                <p>
                  Are you sure you want to approve <strong>all labels</strong>?
                  <br />
                  This action cannot be undone.
                </p>
              </div>

              <div className="modal-footer">
                <Button
                  variant="secondary"
                  onClick={() => setShowApproveAllModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  variant="success"
                  disabled={bulkLoading}
                  onClick={handleApproveAll}
                >
                  {bulkLoading ? "Approving..." : "Yes, Approve All"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ViewUserModal
        show={showViewModal}
        onClose={() => setShowViewModal(false)}
        data={selectedWarehouse}
        title="Warehouse Details"
      />

      <div className="page-toolbar px-xl-4 px-sm-2 px-0 py-3">
        <div className="container-fluid">
          <ol className="breadcrumb bg-transparent mb-0">
            <li className="breadcrumb-item">
              <Link className="text-secondary" to="/">
                Home
              </Link>
            </li>
            <li className="breadcrumb-item active">Aprrove Workflow</li>
            <li className="breadcrumb-item active">Warehouse List</li>
          </ol>
        </div>
      </div>

      <div className="container-fluid mt-3">
        <div className="card">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h6 className="m-0">Workflow Label List</h6>

            <div className="d-flex gap-2">
              {ability.can("updateWorkflowStatus", "all") && (
                <Button
                  variant="success"
                  disabled={!trackingList.length || bulkLoading}
                  onClick={() => setShowApproveAllModal(true)}
                >
                  Approve All
                </Button>
              )}

              <Button onClick={() => navigate("/workflow-view")}>
                Back
              </Button>
            </div>
          </div>

          <div className="card-body">
            <div className="row mb-3">
              <div className="col-md-6">
                <input
                  className="form-control"
                  placeholder="Search label..."
                  value={searchText}
                  onChange={handleSearch}
                />
              </div>
            </div>

            <DataTable
              columns={columns}
              data={filteredProducts}
              pagination
              paginationServer
              paginationTotalRows={pagination.total}
              paginationPerPage={pagination.per_page}
              onChangePage={handlePageChange}
              highlightOnHover
              progressPending={loading}
              progressComponent={<Loading />}
              noDataComponent={
                <div className="py-4 text-center">
                  {searchText
                    ? "No workflow found to approve"
                    : "No worflow available for approval yet."}
                </div>
              }
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default ApproveWorkflow;